import { useEffect, useMemo, useState, type ReactNode } from 'react'
import { collection, doc, limit, onSnapshot, orderBy, query, updateDoc, where } from 'firebase/firestore'
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded'
import AssignmentTurnedInRoundedIcon from '@mui/icons-material/AssignmentTurnedInRounded'
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded'
import EditRoundedIcon from '@mui/icons-material/EditRounded'
import FactCheckRoundedIcon from '@mui/icons-material/FactCheckRounded'
import PlayCircleOutlineRoundedIcon from '@mui/icons-material/PlayCircleOutlineRounded'
import PreviewRoundedIcon from '@mui/icons-material/PreviewRounded'
import SendRoundedIcon from '@mui/icons-material/SendRounded'
import { useAuth } from '../../context/useAuth'
import { useToast } from '../../context/useToast'
import { useWorkspace } from '../../context/useWorkspace'
import { useNls } from '../../nls/nlsProvider'
import { firestoreDb } from '../../services/firebase'
import { useWorkspaceMemberNames } from '../../hooks/useWorkspaceMemberNames'
import { formatPersonAnswer } from '../../utils/forms/personDisplay'
import type { FormDefinition, FormField, FormResponse, FormResponseStatus, FormUploadedFile } from '../../types/forms'
import { Button, IconButton, Pressable } from '../Common'
import { Loader } from '../Common/Loader'
import type { FormsNlsT } from './FormsNls'
import { formatFormsResponseCount, getFormsFieldTypeLabel, getFormsModeLabel, getFormsResponseStatusLabel } from './FormsNls'
import { FormRenderer } from './FormRenderer'

export type FormExperienceMode = 'PREVIEW' | 'FILL' | 'RESPONSES'

type FormExperienceScreenProps = {
  definition: FormDefinition
  initialMode?: FormExperienceMode
  canEdit?: boolean
  canRequest?: boolean
  canRespond?: boolean
  canReviewResponses?: boolean
  onBack?: () => void
  onEdit?: () => void
  onRequest?: () => void
  onSubmitted?: (responseId: string) => void
  onDirtyChange?: (dirty: boolean) => void
  compact?: boolean
  showInlineActions?: boolean
}

const toDate = (value: FormResponse['submittedAt']) => {
  if (!value) return null
  return value instanceof Date ? value : value.toDate()
}

const fieldById = (definition: FormDefinition) => new Map(definition.fields.map((field) => [field.id, field]))

export const FormExperienceScreen = ({
  definition,
  initialMode = 'PREVIEW',
  canEdit = false,
  canRequest = false,
  canRespond = true,
  canReviewResponses = false,
  onBack,
  onEdit,
  onRequest,
  onSubmitted,
  onDirtyChange,
  compact = false,
  showInlineActions = false,
}: FormExperienceScreenProps) => {
  const { t } = useNls()
  const { activeWorkspaceId } = useWorkspace()
  const workspaceId = definition.workspaceId || activeWorkspaceId || ''
  void workspaceId

  const [mode, setMode] = useState<FormExperienceMode>(initialMode)
  const activeMode = mode === 'FILL' && (!canRespond || definition.lifecycleStatus !== 'ACTIVE' || definition.isLocked)
    ? 'PREVIEW'
    : mode
  const responseTitle = formatFormsResponseCount(definition.submissionCount, t)

  useEffect(() => setMode(initialMode), [definition.id, initialMode])

  return (
    <section className={`formExperience${compact ? ' isCompact' : ''}`} aria-label={`${definition.title} form experience`}>
      <div className="formExperienceMetaStrip">
        <p>{definition.description || 'Structured intake form ready for workspace submission.'}</p>
        <div className="formExperienceStats">
          <span><strong>{definition.fields.length}</strong> fields</span>
          <span><strong>{responseTitle}</strong></span>
          <span><strong>{definition.lifecycleStatus}</strong></span>
        </div>
        {showInlineActions && (
          <div className="formExperienceInlineActions">
            {onBack && <Button variant="tertiary" icon={<ArrowBackRoundedIcon />} onClick={onBack}>{t('NLSCommonActionBack')}</Button>}
            {canRequest && definition.lifecycleStatus === 'ACTIVE' && <Button variant="secondary" icon={<SendRoundedIcon />} onClick={onRequest}>{t('NLSFormsActionRequestMembers')}</Button>}
            {canEdit && definition.lifecycleStatus !== 'ARCHIVED' && <IconButton variant="secondary" icon={<EditRoundedIcon />} label="Edit" onClick={onEdit} />}
            {definition.lifecycleStatus === 'ACTIVE' && <Button variant="primary" icon={<PlayCircleOutlineRoundedIcon />} disabled={!canRespond} onClick={() => setMode('FILL')}>{t('NLSFormsActionOpenLiveForm')}</Button>}
          </div>
        )}
      </div>

      <nav className="formExperienceTabs" aria-label="Form experience mode">
        {(['PREVIEW', 'FILL', 'RESPONSES'] as FormExperienceMode[]).map((candidate) => {
          const disabled = candidate === 'FILL'
            ? !canRespond || definition.lifecycleStatus !== 'ACTIVE' || definition.isLocked
            : candidate === 'RESPONSES' && definition.submissionCount === 0 && !canReviewResponses
          const Icon = candidate === 'PREVIEW' ? PreviewRoundedIcon : candidate === 'FILL' ? AssignmentTurnedInRoundedIcon : FactCheckRoundedIcon
          return (
            <Pressable key={candidate} aria-selected={activeMode === candidate} disabled={disabled} onClick={() => setMode(candidate)}>
              <Icon />
              <span>{getFormsModeLabel(candidate, t)}</span>
            </Pressable>
          )
        })}
      </nav>

      <div className="formExperienceBody">
        <aside className="formExperienceOutline" aria-label="Form outline">
          <span className="formsEyebrow">Structure</span>
          {definition.fields.length === 0 ? <p>No fields yet.</p> : definition.fields.map((field, index) => (
            <Pressable key={field.id}>
              <span>{index + 1}</span>
              <strong>{field.label}</strong>
              <small>{getFormsFieldTypeLabel(field, t)}</small>
            </Pressable>
          ))}
        </aside>

        <main className="formExperienceStage">
          {activeMode === 'RESPONSES' ? (
            <FormResponseReview definition={definition} canReviewResponses={canReviewResponses} />
          ) : activeMode === 'FILL' ? (
            <div className="formExperienceCard">
              <FormRenderer definition={definition} onSubmitted={onSubmitted} onDirtyChange={onDirtyChange} />
            </div>
          ) : (
            <div className="formExperienceCard">
              <div className="formExperiencePreviewBanner">
                <CheckCircleRoundedIcon />
                <span><strong>Final view</strong><small>This is the exact live layout responders will use.</small></span>
              </div>
              <FormRenderer definition={definition} disabled onDirtyChange={onDirtyChange} />
            </div>
          )}
        </main>
      </div>
    </section>
  )
}

const FormResponseReview = ({
  definition,
  canReviewResponses,
}: {
  definition: FormDefinition
  canReviewResponses: boolean
}) => {
  const { user } = useAuth()
  const { memberNames, loaded } = useWorkspaceMemberNames(definition.workspaceId)
  const { showToast } = useToast()
  const { locale, t } = useNls()
  const [responses, setResponses] = useState<FormResponse[]>([])
  const [loading, setLoading] = useState(true)
  const [updatingStatus, setUpdatingStatus] = useState<FormResponseStatus | null>(null)
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const fields = useMemo(() => fieldById(definition), [definition])
  const selected = responses.find((response) => response.id === selectedId) ?? responses[0] ?? null
  const updateStatus = async (status: FormResponseStatus) => {
    if (!selected || !canReviewResponses) return
    setUpdatingStatus(status)
    try {
      await updateDoc(doc(firestoreDb, 'form_responses', selected.id), { status })
      showToast(`Response marked ${status.toLocaleLowerCase()}.`, 'success')
    } catch (error) {
      showToast(error instanceof Error ? error.message : 'Could not update response status.', 'error')
    } finally {
      setUpdatingStatus(null)
    }
  }

  useEffect(() => {
    if (!user) { setLoading(false); return }
    setLoading(true)
    const constraints = [
      where('workspaceId', '==', definition.workspaceId),
      where('formId', '==', definition.id),
      ...(canReviewResponses ? [] : [where('submittedBy', '==', user.uid)]),
      orderBy('submittedAt', 'desc'),
      limit(100),
    ]
    return onSnapshot(query(collection(firestoreDb, 'form_responses'), ...constraints), (snapshot) => {
      const next = snapshot.docs.map((entry) => ({ id: entry.id, ...entry.data() }) as FormResponse)
      setResponses(next)
      setSelectedId((current) => current && next.some((response) => response.id === current) ? current : next[0]?.id ?? null)
      setLoading(false)
    }, (error) => {
      setLoading(false)
      showToast(error.message, 'error')
    })
  }, [canReviewResponses, definition.id, definition.workspaceId, showToast, user])

  if (loading) return <div className="formsEmptyState appInlineLoadingState"><Loader size={140} label={t('NLSFormsLoadingResponses')} /><span>Reading the response ledger.</span></div>
  if (responses.length === 0) return <div className="formsEmptyState"><FactCheckRoundedIcon /><strong>{t('NLSFormsEmptyNoResponsesYetTitle')}</strong><span>{t('NLSFormsEmptyNoResponsesYetBody')}</span></div>

  return (
    <div className="formResponseReview">
      <section className="formResponseList" aria-label="Responses">
        <span className="formsEyebrow">Response ledger</span>
        {responses.map((response) => {
          const submittedAt = toDate(response.submittedAt)
          return (
            <Pressable key={response.id} className={selected?.id === response.id ? 'isSelected' : ''} onClick={() => setSelectedId(response.id)}>
              <FactCheckRoundedIcon />
              <span><strong>{response.submittedByName ?? response.submittedBy}</strong><small>{submittedAt ? submittedAt.toLocaleString(locale) : t('NLSFormsLabelPendingTimestamp')}</small></span>
              <em>{getFormsResponseStatusLabel(response.status, t)}</em>
            </Pressable>
          )
        })}
      </section>
      <section className="formResponseDetail" aria-label="Selected response">
        {selected ? (
          <>
            <header>
              <div><span className="formsEyebrow">Submitted response</span><h3 className="formResponseDetailTitle">{selected.submittedByName ?? selected.submittedBy}</h3><p>{toDate(selected.submittedAt)?.toLocaleString(locale) ?? t('NLSFormsLabelPendingTimestamp')} · {getFormsResponseStatusLabel(selected.status, t)}</p></div>
              {canReviewResponses && (
                <div className="formResponseStatusActions">
                  {(['PENDING', 'REVIEWED', 'EXPORTED'] as FormResponseStatus[]).map((status) => (
                    <Button key={status} variant={selected.status === status ? 'primary' : 'tertiary'} size="sm" disabled={selected.status === status || updatingStatus !== null} onClick={() => void updateStatus(status)}>
                      {updatingStatus === status ? t('NLSFormsActionSaving') : getFormsResponseStatusLabel(status, t)}
                    </Button>
                  ))}
                </div>
              )}
            </header>
            <div className="formResponseFields">
              {definition.fields.map((field) => (
                <article key={field.id}>
                  <span>{field.label}</span>
                  <strong>{renderResponseValue(field, selected.data[field.id], fields, t, memberNames, loaded)}</strong>
                </article>
              ))}
            </div>
          </>
        ) : null}
      </section>
    </div>
  )
}

const renderResponseValue = (field: FormField, value: unknown, fields: Map<string, FormField>, t: FormsNlsT, memberNames?: Map<string, string>, loaded?: boolean): ReactNode => {
  void fields
  if (value === undefined || value === null || value === '') return t('NLSFormsLabelNoResponse')
  if (field.type === 'CHECKBOX') return value === true ? t('NLSFormsLabelYes') : t('NLSFormsLabelNo')
  if (field.type === 'PERSON') {
    return formatPersonAnswer(value, memberNames, {
      formerLabel: t('NLSFormsFormerMember'),
      emptyLabel: t('NLSFormsLabelNoResponse'),
      loading: !loaded,
    })
  }
  if (field.type === 'FILE_UPLOAD') {
    const files = Array.isArray(value) ? value.filter((entry): entry is FormUploadedFile => Boolean(entry && typeof entry === 'object' && 'fileName' in entry)) : []
    return files.length ? (
      <span className="formResponseFiles">
        {files.map((file) => file.isUnavailable
          ? <span key={file.fileId || file.storagePath}>{file.fileName} · {file.unavailableReason || t('NLSFormsLabelAttachmentUnavailable')}</span>
          : <a key={file.fileId || file.storagePath} href={file.downloadUrl} target="_blank" rel="noreferrer">{file.fileName}</a>)}
      </span>
    ) : t('NLSFormsLabelNoFilesUploaded')
  }
  if (Array.isArray(value)) return value.join(', ')
  return String(value)
}
